import { BaseNode, type NodeConfig } from 'mflows'
import type { Variable } from 'mflows'
import { variablesState } from 'mflows'

interface VariableGetConfig extends NodeConfig {
  scope?: 'global' | 'workflow'
  key?: string
}

interface VariableGetValue {
  key: string
  type: Variable['type']
  value: any
  scope: 'global' | 'workflow'
}

export class VariableGet extends BaseNode<VariableGetConfig, VariableGetValue> {
  static NodeType = '变量读取'

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'execute', name: '执行', allowTypes: ['flow'], dataType: 'flow' },
      { id: 'variable-key', name: '变量名', allowTypes: ['text', 'string'], dataType: 'string' },
      { id: 'scope', name: '作用域', allowTypes: ['text', 'string'], dataType: 'string' }
    ])

    this.setOutputPorts([
      { id: 'variable', name: '变量', dataType: 'object' },
      { id: 'variable-error', name: '错误', dataType: 'object' },
      { id: 'next', name: '下一步', dataType: 'flow' }
    ])
  }

  protected receiveData(pointId: string, data: unknown): void {
    switch (pointId) {
      case 'variable-key':
        if (typeof data === 'string' && data) {
          this.updateSettings({ key: data })
        }
        break
      case 'scope':
        if (data === 'global' || data === 'workflow') {
          this.updateSettings({ scope: data })
        }
        break
    }

    const inputPoint = this.findInputPort(pointId)
    if (inputPoint?.dataType === 'flow') {
      this.executeConfigUpdate()
    }
  }

  protected checkConfig(): boolean {
    return Boolean(this.settings.key && (this.settings.scope === 'global' || this.settings.scope === 'workflow'))
  }

  protected async processConfigUpdate(settings: VariableGetConfig): Promise<any> {
    try {
      const { key, scope } = settings
      if (!key || !(scope === 'global' || scope === 'workflow')) {
        const err = { message: '请配置变量名与作用域', key, scope }
        return { value: err as any, error: new Error(JSON.stringify(err)), state: 'error', updateTime: Date.now() }
      }

      const list = scope === 'global' ? variablesState.globalVariables : variablesState.workflowVariables
      const found = list.find(v => v.key === key) || null
      if (!found) {
        const err = { message: '变量不存在', key, scope }
        return { value: err as any, error: new Error(JSON.stringify(err)), state: 'error', updateTime: Date.now() }
      }

      const value: VariableGetValue = { key: found.key, type: found.type, value: found.value, scope }
      Object.assign(this.nodeData, { value, error: undefined, state: 'dataChange', updateTime: Date.now() })
      return this.nodeData
    } catch (error) {
      return {
        value: { message: String(error) } as any,
        error: error instanceof Error ? error : new Error(String(error)),
        state: 'error',
        updateTime: Date.now()
      }
    }
  }

  protected getOutputPortActions(): { dataOnly?: string[]; triggerFlow?: string[] } {
    if (this.getState() === 'error') {
      return { dataOnly: ['variable-error'], triggerFlow: [] }
    }
    return { dataOnly: ['variable'], triggerFlow: ['next'] }
  }
}